<template>
  <v-container>
    <v-row>
      <v-col class="px-6" cols="12">
        <!-- ================================================================= -->
        <!-- HEADER & GLOBAL ACTIONS                                         -->
        <!-- ================================================================= -->
        <v-row align="center">
          <v-col cols="12" md="6">
            <h2 class="mt-5">Checklisten prüfen</h2>
          </v-col>
          <v-col cols="12" md="6" class="text-md-right">
            <!--
              CORRECTION: This button now acts on ALL visible lists, not just completed ones.
              Its visibility and text are updated to reflect this.
            -->
            <v-btn
                v-if="allVisibleLists.length > 0"
                color="secondary"
                large
                :loading="isArchiving"
                @click="confirmArchiveAllVisible()"
            >
              <v-icon left>mdi-archive-arrow-down-outline</v-icon>
              Alle sichtbaren Listen archivieren
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <h3 class="my-4">Zu prüfende Checklisten nach Fahrzeugen:</h3>
            <v-select
                :items="filters"
                v-model="filter"
                item-text="name"
                item-value="key"
                label="Filter"
                class="my-4"
                @change="getVehicles"
            ></v-select>

            <v-alert v-if="!vehicles || vehicles.length === 0" type="info">
              Für den gewählten Filter gibt es keine zu prüfenden Checklisten.
            </v-alert>

            <!-- Original UI Layout Restored -->
            <v-expansion-panels v-else v-model="panel">
              <v-expansion-panel v-for="vehicle in vehicles" :key="vehicle._id">
                <v-expansion-panel-header>
                  <v-row no-gutters>
                    <v-col cols="9">{{ vehicle.name }}</v-col>
                    <v-col cols="3" class="text--secondary">
                      <v-row no-gutters style="width: 100%">
                        <v-col cols="6"></v-col>
                        <v-col cols="6">
                          <span>{{ getItemCount(vehicle.plists).done }}</span>
                          <v-icon color="success">mdi-check-circle</v-icon>
                          <span class="ml-2">{{ getItemCount(vehicle.plists).error }}</span>
                          <v-icon color="red">mdi-close-circle</v-icon>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-expansion-panels accordion>
                    <v-expansion-panel v-for="list in vehicle.plists" :key="list._id">
                      <v-expansion-panel-header>
                        <v-row no-gutters>
                          <v-col cols="1">
                            <v-icon v-if="list.uncheckedItems" color="red">mdi-clipboard-list</v-icon>
                            <v-icon v-else color="success">mdi-clipboard-list</v-icon>
                          </v-col>
                          <v-col cols="11">{{ list.title }}</v-col>
                        </v-row>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row no-gutters>
                          <v-col cols="5">
                            <p v-if="!list.uncheckedItems">Alle Checklisten-Items sind in Ordnung.</p>
                            <div v-else>
                              <div v-for="item in list.items" :key="item._id">
                                <div v-if="!item.is_ok">
                                  <p>
                                    <v-icon>mdi-close-circle-outline</v-icon>
                                    <strong>{{ item.title }}</strong>: {{ item.remark }}
                                    <span v-if="item.editor">({{ item.editor }})</span>
                                  </p>
                                </div>
                              </div>
                            </div>
                          </v-col>
                          <v-spacer></v-spacer>
                          <v-divider vertical class="mx-4"></v-divider>
                          <v-col cols="4" v-if="list.editor && list.updatedAt">
                            Author: <strong>{{ list.author }}</strong><br>
                            letzer Bearbeiter: <strong>{{ list.editor }}</strong><br>
                            Datum: <strong>{{ list.updatedAt | moment }}</strong>
                          </v-col>
                        </v-row>
                        <v-card-actions>
                          <v-spacer></v-spacer>
                          <!-- The single archive button is still smart and only shows for complete lists -->
                          <v-btn text color="secondary" v-if="!list.uncheckedItems" @click="confirmArchive(list)">Archivieren</v-btn>
                          <v-btn text color="primary" @click="openList(list)">Anzeigen</v-btn>
                        </v-card-actions>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <!-- ================================================================= -->
    <!-- DIALOGS                                                         -->
    <!-- ================================================================= -->
    <v-dialog v-model="dialog.confirmArchive" persistent max-width="400">
      <v-card v-if="selectedList">
        <v-card-title class="headline">Checkliste archivieren?</v-card-title>
        <v-card-text>Soll die Checkliste "{{ selectedList.title }}" wirklich archiviert werden?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog.confirmArchive = false">Abbrechen</v-btn>
          <v-btn color="secondary" text :loading="isArchiving" @click="archiveSingleList()">Archivieren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog.confirmArchiveAll" persistent max-width="500">
      <v-card v-if="listsToArchive.length > 0">
        <v-card-title class="headline">{{ listsToArchive.length }} Checklisten archivieren?</v-card-title>
        <v-card-text>
          Sind Sie sicher, dass Sie alle aktuell sichtbaren Checklisten archivieren möchten?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog.confirmArchiveAll = false">Abbrechen</v-btn>
          <v-btn color="secondary" text :loading="isArchiving" @click="archiveAllVisible()">Archivieren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.state" :timeout="snack.timeout" :color="snack.color" top right>{{ snack.message }}</v-snackbar>
  </v-container>
</template>
<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "Review",
  data() {
    return {
      vehicles: [],
      selectedList: null,
      listsToArchive: [],
      panel: null,
      isArchiving: false,
      filters: [
        { name: 'Alle', key: 'all' },
        { name: 'Nur Komplette', key: 'complete' },
        { name: 'Nur Fehlerhafte', key: 'incomplete' }
      ],
      filter: 'all',
      dialog: { confirmArchive: false, confirmArchiveAll: false },
      snack: { state: false, color: 'success', timeout: 3000, message: '' }
    }
  },
  computed: {
    // This logic is correct. It finds all lists that are currently visible
    // and then filters them to find the ones eligible for archiving.
    allVisibleLists() {
      if (!this.vehicles) return [];
      return this.vehicles.flatMap(vehicle => vehicle.plists);
    },
    // Helper property to show a warning in the confirmation dialog.
    hasIncompleteListsInSelection() {
      if (!this.listsToArchive) return false;
      return this.listsToArchive.some(list => list.uncheckedItems);
    }
  },
  mounted() {
    this.getVehicles();
  },
  filters: {
    moment: date => moment(date).format('DD.MM.YY HH:mm'),
  },
  methods: {
    getVehicles() {
      axios.get(`/pchecklists/vehicles/review/${this.filter}`)
          .then((response) => { this.vehicles = response.data; })
          .catch(() => this.showSnackbar("Checklisten konnten nicht geladen werden.", "error"));
    },
    getItemCount(lists) {
      if (!lists) return { done: 0, error: 0 };
      return lists.reduce((acc, list) => {
        list.uncheckedItems ? acc.error++ : acc.done++;
        return acc;
      }, { done: 0, error: 0 });
    },
    openList(list) {
      this.$router.push({name: 'PopulateChecklist', params: {id: list._id}, query: { newList: 'false', review: 'true' }});
    },

    confirmArchive(list) {
      this.selectedList = list;
      this.dialog.confirmArchive = true;
    },

    confirmArchiveAllVisible() {
      // CORRECTION: Use the new computed property to get ALL visible lists.
      this.listsToArchive = this.allVisibleLists;
      if (this.allVisibleLists && this.listsToArchive.length > 0) {
        this.dialog.confirmArchiveAll = true;
      } else {
        // This case should rarely happen if the button is only visible when lists exist.
        this.showSnackbar("Keine Checklisten zum Archivieren gefunden.", "info");
      }
    },

    archiveAllVisible() {
      this.isArchiving = true;
      const idsToArchive = this.listsToArchive.map(list => list._id);
      axios.patch('/pchecklists/batch/state', { ids: idsToArchive, state: 'archived' })
          .then((response) => {
            const count = response.data.details ? (response.data.details.nModified || 0) : idsToArchive.length;
            this.showSnackbar(`${count} Checkliste(n) erfolgreich archiviert.`);
            this.getVehicles(); // Refresh the view
          })
          .catch(() => this.showSnackbar("Fehler beim Archivieren der Checklisten.", "error"))
          .finally(() => {
            this.isArchiving = false;
            this.dialog.confirmArchiveAll = false;
            this.listsToArchive = [];
          });
    },

    archiveSingleList() {
      this.isArchiving = true;
      axios.patch(`/pchecklists/${this.selectedList._id}`, { state: 'archived' })
          .then(() => {
            this.showSnackbar('Checkliste wurde erfolgreich archiviert.');
            this.getVehicles();
          })
          .catch(() => this.showSnackbar("Fehler beim Archivieren der Checkliste.", "error"))
          .finally(() => {
            this.isArchiving = false;
            this.dialog.confirmArchive = false;
          });
    },

    showSnackbar(message, color = 'success') {
      this.snack.message = message;
      this.snack.color = color;
      this.snack.state = true;
    }
  }
}
</script>